import React, { useState } from 'react';
import './OhMyFinAISuite.css';

interface UserProps {
  isAdmin?: boolean;
}

export const OhMyFinAISuite: React.FC<UserProps> = ({ isAdmin = false }) => {
  const [activeTab, setActiveTab] = useState<'sanctions' | 'banks' | 'country' | 'fx'>('sanctions');
  
  // State for Sanctions screening
  const [sanctionQuery, setSanctionQuery] = useState('');
  const [sanctionType, setSanctionType] = useState('individual');
  const [sanctionResult, setSanctionResult] = useState<any>(null);
  const [loadingSanctions, setLoadingSanctions] = useState(false);

  // State for Bank search
  const [bankQuery, setBankQuery] = useState('DEUTSCHE');
  const [bankResult, setBankResult] = useState<any>(null);
  const [loadingBank, setLoadingBank] = useState(false);

  // State for Country profile
  const [countryCode, setCountryCode] = useState('US');
  const [countryResult, setCountryResult] = useState<any>(null);
  const [loadingCountry, setLoadingCountry] = useState(false);

  // State for Admin-only FX rates
  const [fxBase, setFxBase] = useState('EUR');
  const [fxTarget, setFxTarget] = useState('USD');
  const [fxResult, setFxResult] = useState<any>(null);
  const [loadingFx, setLoadingFx] = useState(false);

  // AI Copilot Drawer State
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotInput, setCopilotInput] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ sender: 'user' | 'assistant'; text: string }>>([
    { sender: 'assistant', text: 'Hello! I am your OhMyFinAI Compliance Assistant. You can ask me to screen an entity, check SWIFT codes, or explain regulatory requirements.' }
  ]);
  const [loadingChat, setLoadingChat] = useState(false);

  // 1. Handle Sanctions Screening
  const handleSanctionsScreen = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!sanctionQuery.trim()) return;

    setLoadingSanctions(true);
    try {
      const res = await fetch('/api/v4/ohmyfin/sanctions/screen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: sanctionQuery,
          entity_type: sanctionType,
          threshold: 0.8
        })
      });
      const data = await res.json();
      if (data.success) {
        setSanctionResult(data.data);
      } else {
        alert(data.message || 'Error running screening');
      }
    } catch (err: any) {
      console.error('Sanctions error:', err);
    } finally {
      setLoadingSanctions(false);
    }
  };

  // 2. Handle Bank Search
  const handleBankSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!bankQuery.trim()) return;

    setLoadingBank(true);
    try {
      const res = await fetch(`/api/v4/ohmyfin/banks?query=${encodeURIComponent(bankQuery)}&limit=10`);
      const data = await res.json();
      if (data.success) {
        setBankResult(data.data);
      }
    } catch (err) {
      console.error('Bank search error:', err);
    } finally {
      setLoadingBank(false);
    }
  };

  // 3. Handle Country Profile Search
  const handleCountryInspect = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!countryCode.trim()) return;

    setLoadingCountry(true);
    try {
      const res = await fetch(`/api/v4/ohmyfin/country/${countryCode.trim()}`);
      const data = await res.json();
      if (data.success) {
        setCountryResult(data.data);
      }
    } catch (err) {
      console.error('Country inspect error:', err);
    } finally {
      setLoadingCountry(false);
    }
  };

  // 4. Handle Admin-Only FX Rates Search
  const handleFxFetch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isAdmin) return;

    setLoadingFx(true);
    try {
      const res = await fetch(`/api/v4/ohmyfin/fx/rates?base=${fxBase}&target=${fxTarget}`);
      const data = await res.json();
      if (data.success) {
        setFxResult(data.data);
      } else {
        alert(data.message || 'Access Denied: Ohmyfin FX rates are visible to Admins only.');
      }
    } catch (err) {
      console.error('FX fetch error:', err);
    } finally {
      setLoadingFx(false);
    }
  };

  // 5. Handle AI Copilot Chat
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!copilotInput.trim()) return;

    const userText = copilotInput;
    setCopilotInput('');
    setChatMessages((prev) => [...prev, { sender: 'user', text: userText }]);
    setLoadingChat(true);

    try {
      const res = await fetch('/api/v4/ohmyfin/copilot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userText })
      });
      const data = await res.json();
      if (data.success && data.data?.reply) {
        setChatMessages((prev) => [...prev, { sender: 'assistant', text: data.data.reply }]);
      } else {
        setChatMessages((prev) => [...prev, { sender: 'assistant', text: 'Sorry, I ran into an error processing your compliance request.' }]);
      }
    } catch (err) {
      setChatMessages((prev) => [...prev, { sender: 'assistant', text: 'Network connection issue with OhMyFinAI service.' }]);
    } finally {
      setLoadingChat(false);
    }
  };

  return (
    <div className="ohmyfin-container">
      {/* Header Banner */}
      <div className="ohmyfin-header">
        <div className="ohmyfin-header-title">
          <span style={{ fontSize: '2rem' }}>🛡️</span>
          <div>
            <h1>OhMyFinAI Compliance & Intelligence</h1>
            <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.8 }}>
              Automated Sanctions Screening (315 Watchlists), SWIFT Bank Search & AI Copilot
            </p>
          </div>
        </div>
        <span className="ohmyfin-badge-verified">Live API v4.0</span>
      </div>

      {/* Tabs Bar */}
      <div className="ohmyfin-tabs">
        <button
          type="button"
          className={`ohmyfin-tab ${activeTab === 'sanctions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sanctions')}
        >
          🛡️ Sanctions & PEP Screener
        </button>
        <button
          type="button"
          className={`ohmyfin-tab ${activeTab === 'banks' ? 'active' : ''}`}
          onClick={() => setActiveTab('banks')}
        >
          🏦 SWIFT / BIC Bank Lookup
        </button>
        <button
          type="button"
          className={`ohmyfin-tab ${activeTab === 'country' ? 'active' : ''}`}
          onClick={() => setActiveTab('country')}
        >
          🌐 Country & IBAN Compliance
        </button>
        <button
          type="button"
          className={`ohmyfin-tab ${activeTab === 'fx' ? 'active' : ''} ${!isAdmin ? 'ohmyfin-tab-admin-only' : ''}`}
          onClick={() => setActiveTab('fx')}
        >
          💱 Treasury FX Rates {isAdmin ? '(Admin)' : '🔒 (Admin Only)'}
        </button>
      </div>

      {/* TAB 1: SANCTIONS & PEP SCREENING */}
      {activeTab === 'sanctions' && (
        <div className="ohmyfin-card">
          <h3>Screen Entity Against 315 Watchlists</h3>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Enter a person or company name to evaluate against OFAC, EU, UK, UN, and PEP databases.
          </p>
          
          <form className="ohmyfin-search-box" onSubmit={handleSanctionsScreen}>
            <input
              type="text"
              className="ohmyfin-input"
              placeholder="Enter entity or person name (e.g. John Smith)..."
              value={sanctionQuery}
              onChange={(e) => setSanctionQuery(e.target.value)}
            />
            <select
              className="ohmyfin-input"
              style={{ width: '160px', flex: 'none' }}
              value={sanctionType}
              onChange={(e) => setSanctionType(e.target.value)}
            >
              <option value="individual">Individual</option>
              <option value="organization">Organization</option>
              <option value="vessel">Vessel</option>
              <option value="aircraft">Aircraft</option>
            </select>
            <button type="submit" className="ohmyfin-btn" disabled={loadingSanctions}>
              {loadingSanctions ? 'Screening...' : 'Run Screen'}
            </button>
          </form>

          {sanctionResult && (
            <div className="ohmyfin-result-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0' }}>Screening Result: {sanctionResult.query}</h4>
                  <span className={`action-badge ${sanctionResult.recommended_action || 'CLEAR'}`}>
                    Action: {sanctionResult.recommended_action || 'CLEAR'}
                  </span>
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.875rem', color: '#64748b' }}>
                  <div>Total Matches: <strong>{sanctionResult.total_matches}</strong></div>
                  <div>Watchlists Searched: <strong>{sanctionResult.lists_searched?.count || 315}</strong></div>
                </div>
              </div>

              {/* Audit Certificate Box */}
              <div className="ohmyfin-cert-box">
                <div style={{ fontWeight: 600, fontSize: '0.9375rem', marginBottom: '0.5rem', color: '#38bdf8' }}>
                  📜 OhMyFinAI Tamper-Evident Compliance Certificate
                </div>
                <div style={{ fontSize: '0.8125rem', lineHeight: 1.6 }}>
                  <div>Report Certificate ID: <code>{sanctionResult.report_id}</code></div>
                  <div>SHA-256 Fingerprint: <code>{sanctionResult.report_hash}</code></div>
                  <div>Timestamp: <code>{sanctionResult.screened_at}</code></div>
                </div>
              </div>

              {/* Matches Table */}
              {sanctionResult.matches && sanctionResult.matches.length > 0 && (
                <div style={{ marginTop: '1.25rem' }}>
                  <h5 style={{ margin: '0 0 0.5rem 0' }}>Designation Matches:</h5>
                  <table className="ohmyfin-table">
                    <thead>
                      <tr>
                        <th>Entity / Alias</th>
                        <th>Watchlist</th>
                        <th>Severity</th>
                        <th>Action</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sanctionResult.matches.slice(0, 5).map((match: any, idx: number) => (
                        <tr key={idx}>
                          <td><strong>{match.name}</strong></td>
                          <td>{match.list_source || match.list_name}</td>
                          <td><span style={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 700 }}>{match.severity}</span></td>
                          <td><span className={`action-badge ${match.action}`}>{match.action}</span></td>
                          <td>{Math.round((match.match_score || 0.9) * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: SWIFT / BANK SEARCH */}
      {activeTab === 'banks' && (
        <div className="ohmyfin-card">
          <h3>SWIFT / BIC Bank Lookup & Sanctions Check</h3>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Verify bank legal names, SWIFT/BIC codes, head office roots, and real-time sanctions flags.
          </p>

          <form className="ohmyfin-search-box" onSubmit={handleBankSearch}>
            <input
              type="text"
              className="ohmyfin-input"
              placeholder="Search by bank name or BIC code (e.g. DEUTSCHE, DEUTDEFF)..."
              value={bankQuery}
              onChange={(e) => setBankQuery(e.target.value)}
            />
            <button type="submit" className="ohmyfin-btn" disabled={loadingBank}>
              {loadingBank ? 'Searching...' : 'Search Banks'}
            </button>
          </form>

          {bankResult && bankResult.banks && (
            <table className="ohmyfin-table">
              <thead>
                <tr>
                  <th>Bank Name</th>
                  <th>SWIFT / BIC</th>
                  <th>Country</th>
                  <th>City</th>
                  <th>HQ</th>
                  <th>Sanctions Check</th>
                </tr>
              </thead>
              <tbody>
                {bankResult.banks.map((b: any, idx: number) => (
                  <tr key={idx}>
                    <td><strong>{b.name}</strong></td>
                    <td><code>{b.swift}</code></td>
                    <td>{b.country}</td>
                    <td>{b.city || 'N/A'}</td>
                    <td>{b.is_headquarter ? '✅ Yes' : 'Branch'}</td>
                    <td>
                      {b.sanctions && !b.sanctions.sdn && !b.sanctions.eu ? (
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>Clear</span>
                      ) : (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>Sanction Flag</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 3: COUNTRY PROFILE */}
      {activeTab === 'country' && (
        <div className="ohmyfin-card">
          <h3>Country Banking & IBAN Regulatory Inspector</h3>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Inspect clearing systems, IBAN structure masks, and FATF AML compliance status per jurisdiction.
          </p>

          <form className="ohmyfin-search-box" onSubmit={handleCountryInspect}>
            <input
              type="text"
              className="ohmyfin-input"
              placeholder="Enter 2-letter ISO country code (e.g. US, DE, GB, NGN)..."
              value={countryCode}
              maxLength={2}
              onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
            />
            <button type="submit" className="ohmyfin-btn" disabled={loadingCountry}>
              {loadingCountry ? 'Inspecting...' : 'Inspect Country'}
            </button>
          </form>

          {countryResult && countryResult.profile && (
            <div className="ohmyfin-result-box">
              <h4>Country Profile: {countryResult.profile.country?.name || countryCode}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong>Currency:</strong> {countryResult.profile.country?.currency} ({countryResult.profile.country?.currency_name})
                </div>
                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong>Financial Regulator:</strong> {countryResult.profile.country?.financial_regulator || 'Central Bank'}
                </div>
                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong>FATF Status:</strong> {countryResult.profile.country?.fatf_status || 'Member'}
                </div>
                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong>IBAN Mandatory:</strong> {countryResult.profile.account_format?.iban_mandatory ? 'Yes' : 'No'}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: TREASURY FX RATES (RESTRICTED TO ADMIN ONLY) */}
      {activeTab === 'fx' && (
        <div className="ohmyfin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Treasury FX Rates Intelligence</h3>
            <span className="action-badge INFORM">Admin Only Reference Data</span>
          </div>

          {!isAdmin ? (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', padding: '1.5rem', borderRadius: '12px', marginTop: '1rem', color: '#92400e' }}>
              <strong>🔒 Access Restricted to Platform Admins:</strong> Ohmyfin FX market reference rates are visible only to Admin users. Regular users view final manually set rates approved by admin.
            </div>
          ) : (
            <div>
              <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                ECB official daily reference rates & market tri-angulated feeds for indicative reference.
              </p>

              <form className="ohmyfin-search-box" onSubmit={handleFxFetch}>
                <input
                  type="text"
                  className="ohmyfin-input"
                  placeholder="Base Currency (e.g. EUR, USD)..."
                  value={fxBase}
                  onChange={(e) => setFxBase(e.target.value.toUpperCase())}
                />
                <input
                  type="text"
                  className="ohmyfin-input"
                  placeholder="Target Currency (e.g. USD, NGN, GBP)..."
                  value={fxTarget}
                  onChange={(e) => setFxTarget(e.target.value.toUpperCase())}
                />
                <button type="submit" className="ohmyfin-btn" disabled={loadingFx}>
                  {loadingFx ? 'Fetching...' : 'Fetch Rate'}
                </button>
              </form>

              {fxResult && (
                <div className="ohmyfin-result-box">
                  <h4>1 {fxResult.base} = {fxResult.rate} {fxResult.target}</h4>
                  <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    Inverse: 1 {fxResult.target} = {fxResult.inverse} {fxResult.base} | Date: {fxResult.rate_date} | Source: {fxResult.source}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Floating AI Copilot Toggle Button */}
      <button
        type="button"
        onClick={() => setCopilotOpen(!copilotOpen)}
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
          color: '#ffffff',
          border: 'none',
          padding: '0.85rem 1.5rem',
          borderRadius: '9999px',
          fontWeight: 700,
          boxShadow: '0 10px 20px rgba(37, 99, 235, 0.3)',
          cursor: 'pointer',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
      >
        💬 OhMyFinAI Copilot
      </button>

      {/* AI Copilot Drawer */}
      {copilotOpen && (
        <div className="ohmyfin-copilot-drawer">
          <div className="ohmyfin-copilot-header">
            <span style={{ fontWeight: 700 }}>🤖 OhMyFinAI Copilot</span>
            <button
              type="button"
              onClick={() => setCopilotOpen(false)}
              style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '1.25rem', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
          <div className="ohmyfin-copilot-messages">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`copilot-msg ${msg.sender}`}>
                {msg.text}
              </div>
            ))}
            {loadingChat && <div className="copilot-msg assistant">Thinking...</div>}
          </div>
          <form className="ohmyfin-copilot-input" onSubmit={handleSendMessage}>
            <input
              type="text"
              className="ohmyfin-input"
              style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
              placeholder="Ask compliance question..."
              value={copilotInput}
              onChange={(e) => setCopilotInput(e.target.value)}
            />
            <button type="submit" className="ohmyfin-btn" style={{ padding: '0.5rem 1rem' }}>
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default OhMyFinAISuite;
